import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Hub } from "aws-amplify/utils";
import {
    confirmResetPassword,
    confirmSignUp,
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    resendSignUpCode,
    resetPassword,
    signIn,
    signInWithRedirect,
    signOut,
    signUp,
    updatePassword,
    updateUserAttributes,
} from "aws-amplify/auth";
import { getProgress as getKanjiMemoryProgress } from "../utils/kanjiMemoryStorage";
import { getProgress as getGrammarProgress } from "../utils/grammarProgressStorage";
import { getProgress as getVocabularyProgress } from "../utils/vocabularyProgressStorage";
import { getDrawCounts as getKanjiDrawCounts } from "../utils/kanjiDrawCountsStorage";
import { getDrawCounts as getKanaDrawCounts } from "../utils/kanaDrawCountsStorage";
import { getNotes as getKanjiMemoryNotes } from "../utils/kanjiMemoryNotesStorage";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import * as analytics from "../utils/analytics";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthUser {
    username: string;
    email?: string;
    displayName: string;
    groups: string[];
    isAdmin: boolean;
    identityId?: string;
}

interface Credentials {
    email: string;
    password: string;
}

interface AuthContextValue {
    status: AuthStatus;
    isSigningOut: boolean;
    user: AuthUser | null;
    getIdToken: () => Promise<string>;
    signIn: (credentials: Credentials) => Promise<void>;
    signUp: (details: Credentials & { displayName: string }) => Promise<unknown>;
    confirmSignUp: (details: { email: string; code: string }) => Promise<void>;
    resendSignUpCode: (details: { email: string }) => Promise<void>;
    signOut: () => Promise<void>;
    requestPasswordReset: (details: { email: string }) => Promise<void>;
    confirmPasswordReset: (details: { email: string; code: string; newPassword: string }) => Promise<void>;
    changePassword: (details: { oldPassword: string; newPassword: string }) => Promise<void>;
    updateDisplayName: (details: { displayName: string }) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function isNamedError(error: unknown, name: string): boolean {
    return error instanceof Error && error.name === name;
}

async function loadCurrentUser() {
    const [{ username }, attributes, session] = await Promise.all([
        getCurrentUser(),
        fetchUserAttributes(),
        fetchAuthSession(),
    ]);

    const rawGroups = session.tokens?.accessToken?.payload?.["cognito:groups"];
    const groups = Array.isArray(rawGroups) ? rawGroups.filter((group): group is string => typeof group === "string") : [];

    return {
        username,
        email: attributes.email,
        displayName: attributes.preferred_username || "",
        groups,
        isAdmin: groups.includes("admin"),
        // The DynamoDB row-level-security policy keys off the Identity Pool's
        // identityId (via the cognito-identity.amazonaws.com:sub condition key),
        // which is NOT the same value as the User Pool's `sub` attribute.
        identityId: session.identityId,
    };
}

// Every local-storage-backed feature that syncs to the cloud (mirrors the
// FEATURE_KEY used by each feature's own hook, e.g. useGrammarProgress).
const GUEST_MERGE_FEATURES = [
    { key: "kanjiMemory", getLocal: getKanjiMemoryProgress },
    { key: "grammarProgress", getLocal: getGrammarProgress },
    { key: "vocabularyProgress", getLocal: getVocabularyProgress },
    { key: "kanjiDrawCounts", getLocal: getKanjiDrawCounts },
    { key: "kanaDrawCounts", getLocal: getKanaDrawCounts },
    { key: "kanjiMemoryNotes", getLocal: getKanjiMemoryNotes },
];

async function mergeGuestProgressIfNeeded(identityId: string): Promise<void> {
    await Promise.all(
        GUEST_MERGE_FEATURES.map(async ({ key, getLocal }) => {
            const localData = getLocal();
            if (!localData || Object.keys(localData).length === 0) return;

            try {
                const remote = await getCloudProgress(identityId, key);
                if (remote?.data && Object.keys(remote.data).length > 0) return;
                await putCloudProgress(identityId, key, localData);
            } catch {
                // Best-effort: if one feature's merge fails, the others still
                // get their chance, and the user keeps their local progress
                // and can still use the app either way.
            }
        })
    );
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [status, setStatus] = useState<AuthStatus>("loading");
    const [user, setUser] = useState<AuthUser | null>(null);
    const mergedRef = useRef(false);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const signOutPending = useRef<Promise<void> | null>(null);

    const getIdToken = useCallback(async () => {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) throw new Error("No authenticated ID token is available.");
        return token;
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const current = await loadCurrentUser();
            setUser(current);
            setStatus("authenticated");
            return current;
        } catch {
            setUser(null);
            setStatus("unauthenticated");
            return null;
        }
    }, []);

    // Cognito's local session can briefly lag both the initial page load and a
    // fresh sign-in; retry silently (without flipping status to
    // "unauthenticated" in between) so the header doesn't flash logged-out
    // buttons before settling into the signed-in state.
    const refreshUserWithRetry = useCallback(async () => {
        for (let attempt = 0; attempt < 4; attempt += 1) {
            try {
                const current = await loadCurrentUser();
                setUser(current);
                setStatus("authenticated");
                return current;
            } catch {
                if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 400));
            }
        }
        setUser(null);
        setStatus("unauthenticated");
        return null;
    }, []);

    useEffect(() => {
        refreshUserWithRetry();

        const unsubscribe = Hub.listen("auth", ({ payload }) => {
            if (payload.event === "signedIn" || payload.event === "signInWithRedirect") {
                refreshUserWithRetry().then((current) => {
                    if (current?.identityId && !mergedRef.current) {
                        mergedRef.current = true;
                        mergeGuestProgressIfNeeded(current.identityId);
                    }
                    if (current?.identityId) {
                        analytics.identify(current.identityId);
                        analytics.track("login_completed", {
                            method: payload.event === "signInWithRedirect" ? "google" : "email",
                        });
                    }
                });
            } else if (payload.event === "signedOut") {
                mergedRef.current = false;
                setUser(null);
                setStatus("unauthenticated");
                analytics.reset();
            }
        });

        return unsubscribe;
    }, [refreshUserWithRetry]);

    const value: AuthContextValue = {
        status,
        isSigningOut,
        user,

        getIdToken,

        async signIn({ email, password }) {
            let result;
            try {
                result = await signIn({ username: email, password });
            } catch (err: unknown) {
                if (!isNamedError(err, "UserAlreadyAuthenticatedException")) throw err;
                // Amplify's local session state is stale/inconsistent with what
                // our own session checks see - clear it locally and retry once.
                try {
                    await signOut();
                } catch {
                    // best-effort local cleanup before retrying
                }
                result = await signIn({ username: email, password });
            }
            if (!result.isSignedIn) {
                throw new Error(`Sign-in did not complete (next step: ${result.nextStep?.signInStep}).`);
            }
        },

        async signUp({ email, password, displayName }) {
            return signUp({
                username: email,
                password,
                options: { userAttributes: { email, preferred_username: displayName } },
            });
        },

        async confirmSignUp({ email, code }) {
            await confirmSignUp({ username: email, confirmationCode: code });
            analytics.track("signup_completed", { method: "email" });
        },

        async resendSignUpCode({ email }) {
            await resendSignUpCode({ username: email });
        },

        async signOut() {
            if (signOutPending.current) return signOutPending.current;
            setIsSigningOut(true);
            const pending = Promise.resolve().then(() => signOut());
            signOutPending.current = pending;
            try {
                await pending;
            } finally {
                signOutPending.current = null;
                setIsSigningOut(false);
            }
        },

        async requestPasswordReset({ email }) {
            await resetPassword({ username: email });
        },

        async confirmPasswordReset({ email, code, newPassword }) {
            await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
        },

        async changePassword({ oldPassword, newPassword }) {
            await updatePassword({ oldPassword, newPassword });
        },

        async updateDisplayName({ displayName }) {
            await updateUserAttributes({ userAttributes: { preferred_username: displayName } });
            await refreshUser();
        },

        async signInWithGoogle() {
            try {
                await signInWithRedirect({ provider: "Google" });
            } catch (err: unknown) {
                if (!isNamedError(err, "UserAlreadyAuthenticatedException")) throw err;
                // Amplify's local session state is stale/inconsistent with what
                // our own session checks see (e.g. leftover from an interrupted
                // sign-in) - clear it locally and retry once.
                try {
                    await signOut();
                } catch {
                    // best-effort local cleanup before retrying
                }
                await signInWithRedirect({ provider: "Google" });
            }
        },
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
